import React, { useReducer, useRef, useState } from "react"
import { PlayBackType } from "~/ui/audio-recorder"
import { useAudioPlayer } from "../../../context"
import { PlayerAction, PlayerState } from "../../../types/player-state"
import { debug, getMediaPath, globalToast } from "~utils"
import { MessageProps } from "../message.props"
import Voice from "./voice"
import MessageSent from "../message-sent"
import MessageReceived from "../message-received"
import { observer } from "mobx-react-lite"


// UI State
interface State {
  ui: PlayerState
  prev: PlayerState | undefined
}

interface Action {
  type: PlayerAction
}

// VoiceMessage 语音消息
export const VoiceMessage: React.FC<MessageProps> = observer(
({ message, onAudioPlay, onAudioResume, onAudioPause, onAudioStop }) => {
    const [length, setLength] = useState<number>(message.attach?.length || 0)
    const [currentPos, setCurrentPos] = useState<number>(0)

    const initialState: State = {
      ui: PlayerState.Stopped,
      prev: undefined,
    }

    // 添加播放器的回调函数
    const onPlayBack = (e: PlayBackType) => {
      debug(`${e.currentPosition}/${e.duration}`)
      if (Number.isNaN(e.duration) || e.currentPosition < 0) return
      // duration 为 0 时 currentPosition>=duration 恒成立，会高频触发 onAudioStop（安卓易卡死）
      if (e.isFinished === true) {
        onAudioStop()
        setCurrentPos(0)
        dispatch({ type: PlayerAction.Stop })
        return
      }
      if (e.duration <= 0) return
      // 语音消息里保存的 length 往往是“按秒向上取整”的值；
      // 播放器返回的真实 duration 可能略小。这里避免播放后时长显示变短。
      if (e.duration > length) setLength(e.duration)
      setCurrentPos(e.currentPosition)
    }
    const reducer = (state: State, action: Action) => {
      const prev = state.ui

      debug(`[${message.message_id}] dispatch action: ${PlayerAction[action.type]}`)
      switch (action.type) {
        case PlayerAction.Download:
          return { ...state, ui: PlayerState.Downloading, prev }
        case PlayerAction.Play:
          return { ...state, ui: PlayerState.Playing, prev }
        case PlayerAction.Stop:
          return { ...state, ui: PlayerState.Stopped, prev }
        case PlayerAction.Pause:
          return { ...state, ui: PlayerState.Paused, prev }
        case PlayerAction.Resume:
          return { ...state, ui: PlayerState.Playing, prev }
        // case PlayerAction.Upload:
        //   return { ...state, ui: PlayerState.Playing, prev }
        default:
          return { ...state }
      }
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const actingRef = useRef(false)

    const runVoiceAction = async (action: () => void | Promise<void>) => {
      if (actingRef.current) return
      actingRef.current = true
      try {
        await action()
      } finally {
        actingRef.current = false
      }
    }

    const audioPlayer = useAudioPlayer()
    const formatTime = (valueMs: number, rounding: "floor" | "ceil" = "floor") => {
      const seconds =
        rounding === "ceil" ? Math.ceil(valueMs / 1000) : Math.floor(valueMs / 1000)
      return audioPlayer.mmss(Math.max(0, seconds))
    }

    // 格式化时间格式，用于显示
    const duration = formatTime(length, "ceil")
    const playTime = formatTime(currentPos, "floor")
    const playable = getMediaPath(message)
    switch (state.ui) {
      case PlayerState.Stopped:
        return (
          <Voice
            isPlaying={false}
            message={message}
            playTime={playTime}
            duration={duration}
            onClick={(e) => {
              e.stopPropagation()
              if (!onAudioPlay) return
              void runVoiceAction(async () => {
                if (playable) {
                  await onAudioPlay(
                    message,
                    {
                      length,
                      currentPos,
                    },
                    () => {
                      dispatch({ type: PlayerAction.Stop })
                    },
                    onPlayBack,
                  )
                  dispatch({ type: PlayerAction.Play })
                } else {
                  dispatch({ type: PlayerAction.Download })
                }
              }).catch((error) => {
                globalToast.show(error?.message || "语音播放失败")
              })
            }}
          />
        )
      case PlayerState.Playing:
        return (
          <Voice
            isPlaying={true}
            message={message}
            playTime={playTime}
            duration={duration}
            onClick={(e) => {
              e.stopPropagation()
              if (!onAudioPause) return
              void runVoiceAction(async () => {
                dispatch({ type: PlayerAction.Pause })
                await onAudioPause()
              })
            }}
          />
        )
      case PlayerState.Paused:
        return (
          <Voice
            isPlaying={false}
            message={message}
            playTime={playTime}
            duration={duration}
            onClick={(e) => {
              e.stopPropagation()
              if (!onAudioResume) return
              void runVoiceAction(async () => {
                dispatch({ type: PlayerAction.Resume })
                await onAudioResume()
              })
            }}
          />
        )
      default:
        return (
          <Voice
            isPlaying={false}
            message={message}
            playTime={playTime}
            duration={duration}
            onClick={(e) => {
              e.stopPropagation()
              if (!onAudioPause) return
              void runVoiceAction(async () => {
                dispatch({ type: PlayerAction.Stop })
                await onAudioPause()
              })
            }}
          />
        )
    }
  },
)
export const VoiceMessageWithContainer: React.FC<MessageProps> = (props) => {
  return (
    <VoiceMessage {...props} />
  )
}
export const WrappedVoiceMessage: React.FC<MessageProps> = (props) => {
  return props.message.byMe ? (
    <MessageSent {...props} Component={VoiceMessageWithContainer} />
  ) : (
    <MessageReceived {...props} Component={VoiceMessageWithContainer} />
  )
}
export default WrappedVoiceMessage
